import React, { useEffect, useState } from 'react';
import { History, Location } from 'history';
import queryString from 'query-string';

import { PageLoadingIndicator } from '@spotify-internal/anchor-ds';
import { COLOR_MUTED_BACKGROUND } from 'components/PaywallsShared/constants';
import { AnchorAPI } from 'modules/AnchorAPI';
import { Metadata } from 'types/Metadata';
import { useT } from 'client/i18n/hooks/useT';
import { Container, Header, Content } from './styles';

export const HELP_URL =
  'https://help.anchor.fm/hc/en-us/requests/new?ticket_form_id=360001589532';

/**
 * Maps to ?type= query param values. What this particular value should render is
 * defined in the ERRORS object below.
 */
export enum ErrorType {
  DEFAULT_ERROR = 'default',
  SPOTIFY_AUTH_ERROR = 'spotify_auth_error',
  SPOTIFY_ACCESS_DENIED = 'access_denied', // Lacks the spotify_ prefix because it comes directly from the oauth flow
}

interface ErrorConfiguration {
  title: string;
  message: string;
}

export const ERROR_ROUTE = '/404';

const isValidErrorType = (
  errorTypeParam: string | string[] | null | undefined
): errorTypeParam is ErrorType =>
  typeof errorTypeParam === 'string' &&
  Object.values(ErrorType).includes(errorTypeParam as ErrorType);

interface PaywallsSubscriptionErrorProps {
  history: History;
  location: Location;
  match: {
    params: {
      vanitySlug: string;
    };
  };
}

export const PaywallsSubscriptionError = ({
  history,
  location: { search },
  match: {
    params: { vanitySlug },
  },
}: PaywallsSubscriptionErrorProps) => {
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const { type } = queryString.parse(search);
  const forwardTo404 = () => history.push(ERROR_ROUTE);
  const errorType = isValidErrorType(type) ? type : ErrorType.DEFAULT_ERROR;
  const t = useT();

  useEffect(() => {
    const getData = async () => {
      try {
        const stationId = await AnchorAPI.getStationIdForVanityName(vanitySlug);
        const metaResponse = await AnchorAPI.getPodcastMetadata(stationId);
        // I think we can eventually remove this check and the metadata call, but this keeps the URL feature-flagged for now
        if (!metaResponse?.stationPaywall?.isSubscriptionEnabled) {
          return forwardTo404();
        }
        setMetadata(metaResponse);
      } catch (err) {
        forwardTo404();
      }
      return null;
    };
    if (vanitySlug) {
      getData();
    }
  }, [vanitySlug]);

  if (!vanitySlug) {
    forwardTo404();
    return null;
  }
  if (!metadata) {
    return <PageLoadingIndicator color={COLOR_MUTED_BACKGROUND} />;
  }

  const error: ErrorConfiguration = { title: '', message: '' };
  switch (errorType) {
    case ErrorType.DEFAULT_ERROR:
      error.title = t(
        'PaywallsSubscriptionError.DefaultErrorTitle',
        'An error has occured',
        'Error title'
      );
      error.message = t(
        'PaywallsSubscriptionError.DefaultErrorMessage',
        'Something went wrong.',
        'Default error message'
      );
      break;
    case ErrorType.SPOTIFY_AUTH_ERROR:
      error.title = t(
        'PaywallsSubscriptionError.AuthenticationErrorTitle',
        'Unable to authenticate',
        'Authentication error title'
      );
      error.message = t(
        'PaywallsSubscriptionError.AuthenticationErrorMessage',
        'Something went wrong and we weren’t able to authenticate your Spotify account.',
        'Spotify authentication error message'
      );
      break;
    case ErrorType.SPOTIFY_ACCESS_DENIED:
      error.title = t(
        'PaywallsSubscriptionError.AccessDeniedErrorTitle',
        'Account not authenticated',
        'Account access error title'
      );
      error.message = t(
        'PaywallsSubscriptionError.AccessDeniedErrorMessage',
        'To listen to the show you subscribed to, you’ll need to authenticate your Spotify account. If you want to do this later, click the link to authenticate in the email we’ve sent you.',
        'Spotify account access error message'
      );
  }

  return (
    <Container>
      <Header>{error.title}</Header>
      <Content>
        {error.message}
        <>
          {' '}
          {t(
            'PaywallsSubscriptionError.HelpMessage',
            'Reach out to us {helpLink} for help.',
            'Help message',
            {
              helpLink: (
                <a href={HELP_URL} target="_blank" rel="noopener noreferrer">
                  {t(
                    'PaywallsSubscriptionError.HelpLinkText',
                    'here',
                    'Help link text'
                  )}
                </a>
              ),
            }
          )}
        </>
      </Content>
    </Container>
  );
};
